import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { StoreHeader } from "@/components/StoreHeader";
import { HeroSection } from "@/components/HeroSection";
import { CollectionSections } from "@/components/CollectionSection";
import { StoreFooter } from "@/components/StoreFooter";
import { SEO } from "@/components/SEO";

import fdaLogo from "@/assets/certifications/FDA.webp";
import fssaiLogo from "@/assets/certifications/FSSAI.webp";
import fsscLogo from "@/assets/certifications/FSSC.webp";
import gmpLogo from "@/assets/certifications/GMP.webp";
import haccpLogo from "@/assets/certifications/HACCP.webp";
import isoLogo from "@/assets/certifications/ISO.webp";
import spiceBoardLogo from "@/assets/certifications/SPICE_BOARD.webp";

const AboutSection = lazy(() =>
  import("@/components/AboutSection").then((m) => ({ default: m.AboutSection })),
);
const TestimonialsCarousel = lazy(() =>
  import("@/components/TestimonialsCarousel").then((m) => ({ default: m.TestimonialsCarousel })),
);
const SignupCTA = lazy(() =>
  import("@/components/SignupCTA").then((m) => ({ default: m.SignupCTA })),
);

const SITE_URL = "https://paramkesar.co";

const Index = () => {
  const orgLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Param Kesar®",
    legalName: "Gourmet Delights LLC",
    url: SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    email: "support@paramkesar.co",
    telephone: "+1-833-986-6699",
    description:
      "Premium Kashmiri saffron, Kahwa, Noon Chai and heritage spices, sourced direct from Kashmir.",
    address: {
      "@type": "PostalAddress",
      streetAddress: "7901 4th St. N. Ste 300",
      addressLocality: "St. Petersburg",
      addressRegion: "FL",
      postalCode: "33702",
      addressCountry: "US",
    },
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: "+1-833-986-6699",
        email: "support@paramkesar.co",
        contactType: "customer support",
        areaServed: "US",
        availableLanguage: ["English"],
        hoursAvailable: {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          opens: "09:00",
          closes: "17:00",
          timeZone: "America/New_York",
        },
      },
    ],
  };

  const siteLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Param Kesar®",
    url: SITE_URL,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
    ],
  };

  const itemListLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Param Kesar® Featured Products",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Kashmiri Saffron", url: `${SITE_URL}/product/kashmiri-saffron` },
      { "@type": "ListItem", position: 2, name: "Kashmiri Kahwa Tea", url: `${SITE_URL}/product/kashmiri-kahwa` },
      { "@type": "ListItem", position: 3, name: "Kashmiri Noon Chai (Pink Tea)", url: `${SITE_URL}/product/kashmiri-noon-chai` },
    ],
  };

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Where does Param Kesar® saffron come from?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "All Param Kesar® saffron is sourced directly from the Pampore region of Kashmir, India — the only place in the world where true Kashmiri Mongra saffron is grown.",
        },
      },
      {
        "@type": "Question",
        name: "Is Param Kesar® saffron lab-tested and certified?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Every batch is lab-tested for purity and meets ISO 3632 Category I standards. Our facility is FDA-registered, FSSAI certified, and follows FSSC 22000, GMP, and HACCP food safety protocols.",
        },
      },
      {
        "@type": "Question",
        name: "How long does shipping take?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "We offer free 2–5 business day shipping on every order within the United States, fulfilled from our St. Petersburg, FL warehouse.",
        },
      },
      {
        "@type": "Question",
        name: "How should I store saffron to keep it fresh?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Store saffron threads in an airtight container, away from light, heat, and humidity. Properly stored, Param Kesar® saffron retains its aroma and color for up to 3 years.",
        },
      },
    ],
  };

  const videoLd = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: "The Param Kesar® Story",
    description:
      "Meet the farmers and artisans behind Param Kesar® — sourcing the world's finest Kashmiri saffron, Kahwa, and Noon Chai direct from Kashmir.",
    thumbnailUrl: [`${SITE_URL}/hero-saffron.webp`],
    uploadDate: "2025-01-01T00:00:00+00:00",
    contentUrl: `${SITE_URL}/videos/story.mp4`,
    publisher: {
      "@type": "Organization",
      name: "Param Kesar®",
      logo: { "@type": "ImageObject", url: `${SITE_URL}/logo.png` },
    },
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Param Kesar® — Premium Kashmiri Saffron, Kahwa & Noon Chai"
        description="Shop authentic Kashmiri saffron, Kahwa tea, and Noon Chai sourced direct from Kashmir. FDA-registered, lab-tested, premium heritage spices with free US shipping."
        canonicalPath="/"
        jsonLd={[orgLd, siteLd, breadcrumbLd, itemListLd, faqLd, videoLd]}
      />
      <StoreHeader />
      <HeroSection />

      {/* Above-the-fold intro copy for SEO */}
      <section aria-labelledby="intro-heading" className="container py-10 md:py-14">
        <div className="max-w-3xl mx-auto text-center space-y-4">
          <h2 id="intro-heading" className="font-display text-2xl md:text-3xl font-semibold">
            Authentic Kashmiri Saffron, Kahwa &amp; Noon Chai — Direct from the Source
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            Param Kesar® brings you the finest <strong>Kashmiri Mongra saffron</strong>, fragrant{" "}
            <strong>Kahwa green tea</strong>, and traditional <strong>Noon Chai (pink tea)</strong>,
            harvested in the valleys of Pampore, Kashmir and shipped from our FDA-registered
            facility in St. Petersburg, Florida. Every batch is lab-tested for ISO 3632 Category I
            purity, hand-graded for color and aroma, and delivered to your door with free 2–5 day
            US shipping.
          </p>
        </div>
      </section>

      <CollectionSections />

      {/* Trust badges */}
      <section aria-labelledby="trust-heading" className="container py-12 md:py-16 border-t border-border">
        <h2 id="trust-heading" className="sr-only">Certifications and food safety</h2>
        <p className="text-center text-xs text-muted-foreground uppercase tracking-[0.2em] mb-8">
          Trusted &amp; Certified
        </p>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-6 items-center max-w-5xl mx-auto">
          {[
            { src: fdaLogo, alt: "FDA registered" },
            { src: fssaiLogo, alt: "FSSAI certified" },
            { src: fsscLogo, alt: "FSSC 22000 certified" },
            { src: gmpLogo, alt: "Good Manufacturing Practice (GMP)" },
            { src: haccpLogo, alt: "HACCP food safety certified" },
            { src: isoLogo, alt: "ISO 9001:2015 certified" },
            { src: spiceBoardLogo, alt: "Spices Board India certified" },
          ].map((c) => (
            <div key={c.alt} className="flex items-center justify-center">
              <img
                src={c.src}
                alt={c.alt}
                title={c.alt}
                loading="lazy"
                decoding="async"
                width={120}
                height={96}
                className="max-h-16 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity"
              />
            </div>
          ))}
        </div>
      </section>

      <Suspense fallback={null}>
        <AboutSection />
      </Suspense>
      <Suspense fallback={null}>
        <TestimonialsCarousel />
      </Suspense>

      {/* Helpful internal links */}
      <section aria-labelledby="explore-heading" className="container py-12 md:py-16">
        <h2 id="explore-heading" className="font-display text-2xl md:text-3xl font-bold text-center mb-8">
          Explore Param Kesar®
        </h2>
        <div className="grid sm:grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto">
          {/* Why Saffron card temporarily disabled */}
          <Link to="/thekesarkitchen" className="rounded-xl border border-border bg-card p-5 hover:border-primary transition-colors">
            <p className="font-display text-lg font-semibold mb-1">The Kesar Kitchen™</p>
            <p className="text-sm text-muted-foreground">Recipes, stories &amp; saffron know-how.</p>
          </Link>
          <Link to="/faq" className="rounded-xl border border-border bg-card p-5 hover:border-primary transition-colors">
            <p className="font-display text-lg font-semibold mb-1">FAQs</p>
            <p className="text-sm text-muted-foreground">Shipping, subscriptions, brewing &amp; more.</p>
          </Link>
          <Link to="/shipping" className="rounded-xl border border-border bg-card p-5 hover:border-primary transition-colors">
            <p className="font-display text-lg font-semibold mb-1">Free US Shipping</p>
            <p className="text-sm text-muted-foreground">2–5 day delivery on every order.</p>
          </Link>
          <Link to="/wholesale" className="rounded-xl border border-border bg-card p-5 hover:border-primary transition-colors">
            <p className="font-display text-lg font-semibold mb-1">Wholesale</p>
            <p className="text-sm text-muted-foreground">Bulk pricing for retailers &amp; resellers.</p>
          </Link>
        </div>
      </section>

      <Suspense fallback={null}>
        <SignupCTA />
      </Suspense>
      <StoreFooter />
    </div>
  );
};

export default Index;
